/**
 * M3 회원 탈퇴 (시안 06).
 *
 *   뒤로 + 「회원 탈퇴」 → 이모(40) 인사 → 「탈퇴하면 지워져요」 목록 카드 + 안내 → 떠나는 이유(선택, 칩 하나)
 *   → 동의 체크(필수) → 아래 고정 「탈퇴하기」(오류색, 체크 전 꺼짐) / 「조금 더 있을게요」(뒤로)
 *
 * - 위기 대화(L1 이상) 뒤 24시간 안이면 맨 위에 안전 카드 한 줄(막지는 않음)
 * - 누르면 버튼에 스피너, 실패하면 토스트 「탈퇴를 마치지 못했어요. 잠시 뒤 다시 해 주세요.」
 * - 성공하면 이 기기 세션을 지우고 M4(스택 비움)
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, LoaderCircle } from 'lucide-react';
import { TopBar } from '../../components/TopBar.jsx';
import { Avatar } from '../../components/Avatar.jsx';
import { SafetyCard } from '../../components/SafetyCard.jsx';
import * as meApi from '../../api/me.js';
import { useGoBack } from '../../hooks/useGoBack.js';
import { useSession } from '../../stores/session.js';

const REASONS = [
    { key: 'rarely', label: '자주 안 쓰게 돼요' },
    { key: 'not_expected', label: '대화가 기대와 달라요' },
    { key: 'too_many_alerts', label: '알림이 많아요' },
    { key: 'record_burden', label: '기록이 남는 게 부담돼요' },
    { key: 'other', label: '다른 이유' },
];
const PROVIDER_LABEL = { email: '이메일', kakao: '카카오', apple: 'Apple', google: 'Google' };
const TOAST_MS = 3200;

export function DeleteAccountPage() {
    const navigate = useNavigate();
    const goBack = useGoBack('/me');
    const clearSession = useSession((state) => state.clearSession);
    const [account, setAccount] = useState(null);
    const [reason, setReason] = useState(null);
    const [agreed, setAgreed] = useState(false);
    const [status, setStatus] = useState('idle');
    const [safetyCollapsed, setSafetyCollapsed] = useState(true);

    useEffect(() => {
        const controller = new AbortController();
        meApi
            .fetchAccount({ signal: controller.signal })
            .then((response) => setAccount(response.account))
            .catch(() => {
                // 이메일 문구로 보여 줍니다
            });
        return () => controller.abort();
    }, []);

    useEffect(() => {
        if (status !== 'error') {
            return undefined;
        }
        const timer = setTimeout(() => setStatus('idle'), TOAST_MS);
        return () => clearTimeout(timer);
    }, [status]);

    async function submit() {
        if (!agreed || status === 'pending') {
            return;
        }
        setStatus('pending');
        try {
            await meApi.deleteAccount(reason);
            clearSession();
            navigate('/goodbye', { replace: true });
        } catch {
            setStatus('error');
        }
    }

    const providerLabel = PROVIDER_LABEL[account?.provider] ?? '이메일';
    const pending = status === 'pending';
    return (
        <div className="flex h-dvh flex-col">
            <TopBar title="회원 탈퇴" onBack={goBack} />
            <main className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-4 pt-5 pb-6">
                {account?.recentCrisis ? (
                    <SafetyCard
                        level="L1"
                        collapsed={safetyCollapsed}
                        onCollapse={() => setSafetyCollapsed(true)}
                        onExpand={() => setSafetyCollapsed(false)}
                    />
                ) : null}
                <div className="flex items-start gap-2.5">
                    <Avatar memberKey="imo" size={40} />
                    <p className="m-0 font-serif text-[16px] leading-[27px]">
                        가는 겨? 서운하네. 그래도 언제든 다시 와. 문은 늘 열어 둘게.
                    </p>
                </div>

                <section className="flex flex-col gap-2">
                    <h2 className="m-0 text-[15px] font-bold">탈퇴하면 지워져요</h2>
                    <ul className="m-0 flex list-none flex-col gap-1.5 rounded-card border border-hairline bg-surface px-4 py-3.5">
                        {[
                            '호칭 · 사주 정보 · 단골 카드',
                            '별채대화 · 1:1 대화 전부',
                            '이모 수첩에 적힌 기억',
                            '상회칠판에 올린 글과 답글',
                        ].map((item) => (
                            <li key={item} className="flex gap-2 text-[14px] leading-[22px]">
                                <span aria-hidden="true" className="text-label-muted">
                                    ·
                                </span>
                                {item}
                            </li>
                        ))}
                    </ul>
                    <span className="text-[12px] leading-[18px] text-text-secondary">
                        지운 기록은 되돌릴 수 없어요. {providerLabel} 계정 연결도 함께 끊겨요. 결제 내역이 있다면 관련
                        법에 따라 정해진 기간 동안만 따로 보관돼요.
                    </span>
                </section>

                <section className="flex flex-col gap-2">
                    <h2 className="m-0 text-[15px] font-bold">
                        떠나는 이유를 알려 주면 고쳐 볼게요{' '}
                        <span className="text-[12px] font-medium text-label-muted">(선택)</span>
                    </h2>
                    <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="떠나는 이유">
                        {REASONS.map((item) => {
                            const selected = reason === item.key;
                            return (
                                <button
                                    key={item.key}
                                    type="button"
                                    role="radio"
                                    aria-checked={selected}
                                    onClick={() => setReason(selected ? null : item.key)}
                                    className={`h-9 rounded-[18px] border px-3.5 text-[13px] font-semibold ${
                                        selected
                                            ? 'border-brand bg-brand-tint text-brand'
                                            : 'border-hairline bg-surface text-text-primary'
                                    }`}
                                >
                                    {item.label}
                                </button>
                            );
                        })}
                    </div>
                </section>

                <label className="flex min-h-11 cursor-pointer items-center gap-2.5 rounded-xl bg-surface-sunken px-3.5 py-3 text-[14px] leading-[22px]">
                    <input
                        type="checkbox"
                        checked={agreed}
                        onChange={(event) => setAgreed(event.target.checked)}
                        className="peer sr-only"
                    />
                    <span
                        aria-hidden="true"
                        className={`flex size-[22px] shrink-0 items-center justify-center rounded-md peer-focus-visible:ring-2 peer-focus-visible:ring-brand ${
                            agreed ? 'bg-brand' : 'border-[1.5px] border-label-muted bg-surface'
                        }`}
                    >
                        {agreed ? <Check size={14} strokeWidth={3} className="text-on-brand" /> : null}
                    </span>
                    위 내용을 확인했고, 모든 기록이 지워지는 데 동의해요.
                </label>

                <div className="flex-1" />
                <div className="flex flex-col gap-2">
                    <button
                        type="button"
                        aria-disabled={!agreed || pending}
                        onClick={submit}
                        className={`flex h-[52px] items-center justify-center gap-2 rounded-card bg-error text-[16px] font-bold text-on-brand ${
                            agreed ? '' : 'opacity-40'
                        }`}
                    >
                        {pending ? (
                            <LoaderCircle size={18} strokeWidth={1.8} className="animate-spin" aria-hidden="true" />
                        ) : null}
                        탈퇴하기
                    </button>
                    <button
                        type="button"
                        onClick={goBack}
                        disabled={pending}
                        className="h-12 text-[15px] font-semibold text-text-primary"
                    >
                        조금 더 있을게요
                    </button>
                </div>
            </main>
            {status === 'error' ? (
                <div
                    role="alert"
                    className="fixed inset-x-0 bottom-24 z-50 mx-auto w-fit max-w-[calc(100%-32px)] rounded-xl bg-text-primary px-4 py-3 text-[14px] text-bg"
                >
                    탈퇴를 마치지 못했어요. 잠시 뒤 다시 해 주세요.
                </div>
            ) : null}
        </div>
    );
}
